import { useState, type CSSProperties, type ChangeEvent, type KeyboardEvent } from 'react';
import { Icon, type IconName } from './Icon';

interface InputProps {
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  icon?: IconName;
  iconRight?: IconName;
  type?: string;
  size?: 'sm' | 'md';
  error?: string | boolean | null;
  disabled?: boolean;
  style?: CSSProperties;
  autoFocus?: boolean;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  name?: string;
  id?: string;
  autoComplete?: string;
}

export function Input({
  value,
  onChange,
  placeholder,
  icon,
  iconRight,
  type = 'text',
  size = 'md',
  error,
  disabled,
  style,
  autoFocus,
  onKeyDown,
  name,
  id,
  autoComplete,
}: InputProps) {
  const [focus, setFocus] = useState(false);
  const h = size === 'sm' ? 32 : 36;
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: h,
        width: '100%',
        background: disabled ? 'var(--bx-surface-2)' : '#fff',
        border:
          '1px solid ' +
          (error
            ? 'var(--bx-error)'
            : focus
              ? 'var(--bx-primary)'
              : 'var(--bx-border)'),
        borderRadius: 10,
        padding: '0 10px',
        gap: 8,
        boxShadow: focus ? '0 0 0 3px rgba(11,67,234,0.16)' : 'none',
        transition: 'border-color 120ms, box-shadow 120ms',
        opacity: disabled ? 0.7 : 1,
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={13} style={{ color: 'var(--bx-text-muted)' }} />}
      <input
        id={id}
        name={name}
        type={type}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        autoFocus={autoFocus}
        disabled={disabled}
        onKeyDown={onKeyDown}
        autoComplete={autoComplete}
        style={{
          flex: 1,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontFamily: 'var(--bx-font-sans)',
          fontSize: 13,
          color: 'var(--bx-text-dark)',
          minWidth: 0,
          fontVariantNumeric: type === 'number' ? 'tabular-nums' : 'normal',
        }}
      />
      {iconRight && <Icon name={iconRight} size={13} style={{ color: 'var(--bx-text-muted)' }} />}
    </div>
  );
}
